/**
 * Avatar rendering utilities
 * Requirements: 5.6, 5.7, 6.5
 */

import type { Position } from '@langton/shared';
import { GridType } from '@langton/shared';

/**
 * Avatar image cache to avoid reloading
 */
const avatarCache = new Map<string, HTMLImageElement>();

/**
 * Load an avatar image from URL
 * Returns cached image if already loaded
 */
export async function loadAvatar(url: string): Promise<HTMLImageElement> {
  const cached = avatarCache.get(url);
  if (cached) {
    return cached;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      avatarCache.set(url, img);
      resolve(img);
    };
    img.onerror = () => reject(new Error(`Failed to load avatar: ${url}`));
    img.src = url;
  });
}

/**
 * Clear the avatar cache
 */
export function clearAvatarCache(): void {
  avatarCache.clear();
}

/**
 * Calculate rotation angle in radians for a given direction and grid type
 * Requirements: 6.5
 * 
 * The avatar image points "up" (negative Y direction, angle = 0).
 * We rotate it to face the direction the ant will move.
 */
export function getRotationAngle(direction: number, gridType: GridType): number {
  switch (gridType) {
    case GridType.Square:
      // 4 directions: 0=up (0°), 1=right (90°), 2=down (180°), 3=left (-90°)
      return (direction * Math.PI) / 2;
    case GridType.Triangular: {
      // Direction points to EDGE the ant will move through
      // For up-triangle (default for this generic function):
      // Edge 0 (left): upper-left, Edge 1 (right): upper-right, Edge 2 (bottom): down
      const triAngles = [
        Math.PI / 6,        // 0: left edge - face upper-left (30°)
        -Math.PI / 6,       // 1: right edge - face upper-right (-30°)
        Math.PI             // 2: bottom edge - face down (180°)
      ];
      return triAngles[direction % 3];
    }
    case GridType.Hexagonal: {
      // 6 directions: 0=E, 1=SE, 2=SW, 3=W, 4=NW, 5=NE
      const hexAngles = [
        Math.PI / 2,           // 0: E - face right (90°)
        2 * Math.PI / 3,       // 1: SE - face lower-right (120°)
        Math.PI - Math.PI / 6, // 2: SW - face lower-left (150°)
        -Math.PI / 2,          // 3: W - face left (-90°)
        -2 * Math.PI / 3,      // 4: NW - face upper-left (-120°)
        -Math.PI / 6           // 5: NE - face upper-right (-30°)
      ];
      return hexAngles[direction % 6];
    }
    default:
      return 0;
  }
}

/**
 * Create a triangular clipping path for avatar
 * Requirements: 5.6
 */
export function createTriangularClipPath(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number
): Path2D {
  const path = new Path2D();
  const halfSize = size / 2;
  const triangleHeight = (size * Math.sqrt(3)) / 2;

  // Equilateral triangle centered at (centerX, centerY)
  path.moveTo(centerX, centerY - triangleHeight / 2);                    // Top
  path.lineTo(centerX + halfSize, centerY + triangleHeight / 2);         // Bottom right
  path.lineTo(centerX - halfSize, centerY + triangleHeight / 2);         // Bottom left
  path.closePath();

  return path;
}

/**
 * Create a hexagonal clipping path for avatar
 * Requirements: 5.7
 */
export function createHexagonalClipPath(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number
): Path2D {
  const path = new Path2D();
  const radius = size / 2;

  // Flat-top hexagon centered at (centerX, centerY)
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 6;
    const hx = centerX + radius * Math.cos(angle);
    const hy = centerY + radius * Math.sin(angle);
    
    if (i === 0) {
      path.moveTo(hx, hy);
    } else {
      path.lineTo(hx, hy);
    }
  }

  path.closePath();
  return path;
}

/**
 * Get the appropriate clipping path for a grid type
 * Returns null for square grids (no clipping needed)
 */
export function getClipPathForGridType(
  ctx: CanvasRenderingContext2D,
  gridType: GridType,
  centerX: number,
  centerY: number,
  size: number
): Path2D | null {
  switch (gridType) {
    case GridType.Triangular:
      return createTriangularClipPath(ctx, centerX, centerY, size);
    case GridType.Hexagonal:
      return createHexagonalClipPath(ctx, centerX, centerY, size);
    case GridType.Square:
    default:
      return null;
  }
}

/**
 * Draw an avatar at the specified position with rotation and optional clipping
 * Requirements: 5.6, 5.7, 6.5
 */
export function drawAvatar(
  ctx: CanvasRenderingContext2D,
  avatar: HTMLImageElement | null,
  position: Position,
  direction: number,
  gridType: GridType,
  cellSize: number
): void {
  const avatarSize = cellSize * 0.8;
  const angle = getRotationAngle(direction, gridType);

  ctx.save();
  ctx.translate(position.x, position.y);
  ctx.rotate(angle);

  // Apply clipping for non-square grids
  const clipPath = getClipPathForGridType(ctx, gridType, 0, 0, avatarSize);
  if (clipPath) {
    ctx.clip(clipPath);
  }

  if (avatar) {
    // Draw the avatar image centered and scaled
    ctx.drawImage(
      avatar,
      -avatarSize / 2,
      -avatarSize / 2,
      avatarSize,
      avatarSize
    );
  } else {
    // Fallback: draw a colored circle
    ctx.fillStyle = '#FF0000';
    ctx.beginPath();
    ctx.arc(0, 0, avatarSize / 2, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

/**
 * Draw avatar with direction indicator (arrow)
 * Useful for debugging or when no avatar image is available
 */
export function drawAvatarWithIndicator(
  ctx: CanvasRenderingContext2D,
  position: Position,
  direction: number,
  gridType: GridType,
  cellSize: number,
  color: string = '#FF0000'
): void {
  const avatarSize = cellSize * 0.8;
  const angle = getRotationAngle(direction, gridType);

  ctx.save();
  ctx.translate(position.x, position.y);
  ctx.rotate(angle);

  // Apply clipping for non-square grids
  const clipPath = getClipPathForGridType(ctx, gridType, 0, 0, avatarSize);
  if (clipPath) {
    ctx.clip(clipPath);
  }

  // Draw circle
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(0, 0, avatarSize / 2, 0, Math.PI * 2);
  ctx.fill();

  // Draw direction arrow (pointing up in local coordinates)
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, avatarSize / 4);
  ctx.lineTo(0, -avatarSize / 4);
  ctx.lineTo(-avatarSize / 8, -avatarSize / 8);
  ctx.moveTo(0, -avatarSize / 4);
  ctx.lineTo(avatarSize / 8, -avatarSize / 8);
  ctx.stroke();

  ctx.restore();
}
